import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {MedicalReportFieldsSchema} from '@/features/medical-reports/formSchemas'
import {PlannedAccessesTreatmentFields} from '@/features/planned-accesses/components/accesses-form/planned-accesses-treatment-fields/PlannedAccessesTreatmentFields'
import {PlannedAccessesTotalCard} from '@/features/planned-accesses/components/planned-accesses-total-card/PlannedAccessesTotalCard'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {MedicalReportProject} from '@/features/medical-reports/types'
import {calculateTotalAccesses} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {useCallback, useEffect} from 'react'
import {FormProvider, useForm, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const MedicalReportTreatmentsFieldsProps = MedicalReportFieldsSchema.pick({
    plannedAccesses: true,
    newProjectDurationDays: true
})
type MedicalReportTreatmentsFieldsProps = z.infer<typeof MedicalReportTreatmentsFieldsProps>

// Type for the nested form structure
const NestedTreatmentFormSchema = z.object({
    inPersonAccesses: MedicalReportFieldsSchema.shape.plannedAccesses.shape.inPersonAccesses
})
type NestedTreatmentFormData = z.infer<typeof NestedTreatmentFormSchema>

export const MedicalReportTreatmentsFields = ({project}: {project: MedicalReportProject}) => {
    const {t} = useTranslation()
    const {control, setValue} = useFormContext<MedicalReportTreatmentsFieldsProps>()
    const newProjectDurationDays = useWatch({control, name: 'newProjectDurationDays'})
    const {data, isSuccess} = useGetPlannedAccesses({
        urlParams: {
            projectId: project.id
        }
    })

    const treatments = useWatch({control, name: 'plannedAccesses.inPersonAccesses'})
    const totalTreatments = treatments?.reduce((acc, curr) => {
        return acc + calculateTotalAccesses(newProjectDurationDays ?? 0, curr.frequency, curr.numberOfAccesses || 0)
    }, 0)

    const handleSetTreatments = useCallback(
        (data: ProjectPlannedAccess) => {
            const treatments = data.plannedAccesses
                .filter(access => access.accessType.groupName === 'treatments')
                .map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: access.numberOfAccesses,
                    frequency: access.frequency ?? 'weekly'
                }))

            if (treatments) {
                setValue('plannedAccesses.inPersonAccesses', treatments, {shouldDirty: true})
            }
        },
        [setValue]
    )

    useEffect(() => {
        if (data && isSuccess) {
            handleSetTreatments(data)
        }
    }, [data, handleSetTreatments, isSuccess])

    return (
        <Flexbox direction="column" gap={6}>
            <PlannedAccessesTotalCard title={t('planned_accesses:total_treatments')} total={totalTreatments} />
            {treatments && treatments.length > 0 && (
                <MedicalReportTreatmentsWrapperFields newProjectDurationDays={newProjectDurationDays ?? 0} />
            )}
        </Flexbox>
    )
}

const MedicalReportTreatmentsWrapperFields = ({newProjectDurationDays}: {newProjectDurationDays: number | null}) => {
    const parentForm = useFormContext<MedicalReportTreatmentsFieldsProps>()

    const nestedForm = useForm<NestedTreatmentFormData>({
        defaultValues: {
            inPersonAccesses: parentForm.watch('plannedAccesses.inPersonAccesses')
        }
    })

    // Watch the nested form data and sync with parent
    const nestedInPersonAccesses = useWatch({
        control: nestedForm.control,
        name: 'inPersonAccesses'
    })

    useEffect(() => {
        parentForm.setValue('plannedAccesses.inPersonAccesses', nestedInPersonAccesses, {
            shouldDirty: true,
            shouldValidate: true
        })
    }, [nestedInPersonAccesses])

    return (
        <FormProvider {...nestedForm}>
            <PlannedAccessesTreatmentFields isLoadingProject={false} projectDurationDays={newProjectDurationDays} />
        </FormProvider>
    )
}
